<template>
  <article
    v-if="isHoursOpen"
    :id="`store-locator-card-${store.id}`"
    :aria-labelledby="`store-locator-card-title-${store.id}`"
    :data-testid="`store-locator-card-${store.id}`"
    class="flex w-full flex-col gap-4 overflow-hidden rounded-lg bg-primary-50 px-4 py-3"
  >
    <div class="flex w-full items-start justify-between">
      <div class="flex flex-col">
        <h3
          :id="`store-locator-card-title-${store.id}`"
          class="whitespace-nowrap text-[24px] font-medium leading-8 text-neutral-900"
        >
          {{ store.name }}
        </h3>
        <p class="whitespace-nowrap text-base font-normal leading-6 text-neutral-900">
          {{ [store.street, store.postalCode, store.city].filter(Boolean).join(' ') }}
        </p>
      </div>
    </div>

    <div class="flex w-full flex-col gap-2">
      <div class="flex items-center">
        <div class="flex items-center gap-2 whitespace-nowrap">
          <span class="text-sm font-medium leading-5 text-primary-500">
            {{ openStatus.isOpen ? $t('storeLocator.openLabel') : $t('storeLocator.closedLabel') }}
          </span>
          <span
            v-if="openStatus.isOpen && openStatus.closingTime"
            class="text-sm font-normal leading-5 text-neutral-900"
          >
            {{ $t('storeLocator.hoursToggleLabel', { time: openStatus.closingTime }) }}
          </span>
        </div>
        <button
          :id="`store-locator-hours-btn-${store.id}`"
          type="button"
          :aria-expanded="true"
          :aria-controls="`store-locator-hours-panel-${store.id}`"
          class="ml-auto rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          @click="handleClose"
        >
          <IconChevron class="size-6 rotate-180 fill-neutral-500" aria-hidden="true" />
          <span class="sr-only">{{ $t('storeLocator.closeHours') }}</span>
        </button>
      </div>

      <div
        :id="`store-locator-hours-panel-${store.id}`"
        role="region"
        :aria-labelledby="`store-locator-hours-btn-${store.id}`"
        class="flex w-full flex-col gap-2 rounded-lg bg-primary-50 text-primary-900"
      >
        <div
          v-for="(dayKey, index) in DAY_KEYS"
          :key="dayKey"
          :class="[
            'flex w-full items-center justify-between text-sm leading-5',
            index === 0 ? 'font-medium' : 'font-normal',
          ]"
        >
          <span>{{ $t(`storeLocator.days.${dayKey}`) }}</span>
          <span>
            <template v-if="hoursByDay[dayKey]?.length">
              {{ hoursByDay[dayKey].map((s) => `${s.start} - ${s.end}`).join(', ') }}
            </template>
            <template v-else>{{ $t('storeLocator.closedToday') }}</template>
          </span>
        </div>
      </div>
    </div>
  </article>

  <article
    v-else
    :id="`store-locator-card-${store.id}`"
    :aria-labelledby="`store-locator-card-title-${store.id}`"
    :data-testid="`store-locator-card-${store.id}`"
    class="flex w-full border-b border-neutral-200 bg-white focus-within:bg-primary-50 focus-within:outline focus-within:outline-2 focus-within:outline-primary-500 hover:bg-primary-50"
  >
    <button
      :id="`store-locator-hours-btn-${store.id}`"
      type="button"
      :aria-expanded="false"
      :aria-controls="`store-locator-hours-panel-${store.id}`"
      class="flex w-full flex-col px-4 py-3 text-left focus-visible:outline-none"
      @click="handleOpen"
    >
      <span class="text-[24px] font-medium leading-8 text-neutral-900">
        {{ store.name }}
      </span>

      <span class="text-base font-normal leading-6 text-neutral-900">
        {{ [store.street, store.postalCode, store.city].filter(Boolean).join(' ') }}
      </span>

      <span class="mt-1 flex items-center gap-2">
        <span class="text-sm font-medium leading-5 text-primary-500">
          {{ openStatus.isOpen ? $t('storeLocator.openLabel') : $t('storeLocator.closedLabel') }}
        </span>
        <span v-if="openStatus.isOpen && openStatus.closingTime" class="text-sm font-normal leading-5 text-neutral-900">
          {{ $t('storeLocator.hoursToggleLabel', { time: openStatus.closingTime }) }}
        </span>
        <IconChevron class="size-6 fill-neutral-500" aria-hidden="true" />
      </span>
    </button>
  </article>
</template>

<script setup lang="ts">
import { IconChevron } from '#components';

import type { StoreLocatorCardEmits, StoreLocatorCardProps } from '~/components/StoreLocator/types';

const DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
const JS_DAY_TO_KEY: Record<number, string> = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const props = defineProps<StoreLocatorCardProps>();
const emit = defineEmits<StoreLocatorCardEmits>();

const isHoursOpen = computed(() => props.activeStoreId === props.store.id);

const handleOpen = (): void => {
  emit('select', props.store.id);
};

const handleClose = (): void => {
  emit('select', '');
};

type Slot = { end: string; start: string };

const hoursByDay = computed((): Record<string, Slot[]> => {
  const result: Record<string, Slot[]> = {};
  for (const slot of props.store.openingHours ?? []) {
    (result[slot.day] ??= []).push({ end: slot.end, start: slot.start });
  }
  return result;
});

const openStatus = computed((): { closingTime?: string; isOpen: boolean } => {
  const now = new Date();
  const dayKey = JS_DAY_TO_KEY[now.getDay()] ?? '';
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const todaySlots = hoursByDay.value[dayKey] ?? [];

  for (const slot of todaySlots) {
    const [startH = 0, startM = 0] = slot.start.split(':').map(Number) as [number, number];
    const [endH = 0, endM = 0] = slot.end.split(':').map(Number) as [number, number];
    if (currentMinutes >= startH * 60 + startM && currentMinutes < endH * 60 + endM) {
      return { closingTime: slot.end, isOpen: true };
    }
  }
  return { isOpen: false };
});
</script>
